import { ApiResult, FieldType, ViewModelBase, observable } from "@shoothill/core";
import {
    RDDV2CategoryDTO,
    RDDV2StatusDTO,
    RDDV2SupplierDTO,
    RDDV2RequestedByDTO,
    UpsertRDDV2AndRelatedRequestDTO,
    RDDV2DTO,
    RDDV2AndRelatedResponseDTO,
    RDDV2FormModel,
    RDDV2ProjectDetailsDTO,
} from "./RDDV2FormModel";
import moment from "moment";
import { ServerViewModel } from "Globals/ViewModels/ServerViewModel";
import { AppUrls } from "AppUrls";
import { runInAction, action, computed } from "mobx";
import { RDDV2FormFileModel, RDDV2FileDTO } from "../Common/RDDV2FormFileModel";
import { RDDV2FormDocumentModel, RDDV2DocumentDTO } from "../Common/RDDV2FormDocumentModel";
import { RDDV2FormDocumentViewModel } from "../Common/RDDV2FormDocumentViewModel";
import { RDDV2FormCategoryDocumentViewModel } from "../Common/RDDV2FormCategoryDocumentViewModel";
import { RDDV2FormFileViewModel } from "../Common/RDDV2FormFileViewModel";
import { RDDV2ViewModel } from "../../RDDV2ViewModel";

export class RDDV2FormViewModel extends ViewModelBase<RDDV2FormModel> {
    constructor(id: string | null, projectId: string | null, ref: string | null, isrevision: string | null) {
        super(new RDDV2FormModel());
        this.setDecorators(RDDV2FormModel);
        this.model.id = id;
        this.model.projectId = projectId!;
        this.loadWithRelated();
    }

    public server: ServerViewModel = new ServerViewModel();

    @observable
    public snackMessage = "";

    @observable
    public snackType = "";

    @observable
    public SNACKSUCCESS = "success";

    @observable
    public SNACKERROR = "error";

    @observable
    public snackbarState = false;

    @observable
    public title: any = null;

    @observable
    public description: any = null;

    @observable
    public statuses = observable<RDDV2StatusDTO>([]);

    @observable
    public supplier: { id: string; displayName: string } | null = null;

    @observable
    public category: { id: string; displayName: string } | null = null;

    @observable
    public status: { id: string; displayName: string } | null = null;

    @observable
    public requestedBy: { id: string; displayName: string } | null = null;

    @observable
    public clientReference: { id: string; displayName: string } | null = null;

    @observable
    public suppliers = observable<RDDV2SupplierDTO>([]);

    @observable
    public categories = observable<RDDV2CategoryDTO>([]);

    @observable
    public requestedByUsers = observable<RDDV2RequestedByDTO>([]);

    @observable
    public rddV2ProjectDetails: RDDV2ProjectDetailsDTO | null = null;

    @observable
    public rddV2ViewModels: RDDV2ViewModel[] = [];

    @observable
    public rddV2FileViewModels: RDDV2FormFileViewModel[] = [];

    @observable
    public rddV2CategoryDocumentViewModels: RDDV2FormCategoryDocumentViewModel[] = [];

    @observable
    public rddV2DocumentViewModels: RDDV2FormDocumentViewModel[] = [];

    @observable
    public rddV2: RDDV2DTO | null = null;

    @observable
    public hasLoaded: boolean = false;

    @computed
    public get getHasLoaded(): boolean {
        return this.hasLoaded;
    }

    @computed
    public get clientReferenceFormatted() {
        if (this.model.clientReference === null) {
            return "";
        } else {
            if (!this.model.revision) {
                return "RDD" + this.model.clientReference;
            } else {
                return "RDD" + this.model.clientReference + "." + this.model.revision ? this.model.revision?.toString() : 0;
            }
        }
    }

    @action
    public getFormTitle(isrevision: string): string {
        if (isrevision === "1") {
            return "RDD" + this.model.getValue("clientReference") + "." + (this.model.revision ? this.model.revision.toString() : "") + " - Edit RDD revision";
        } else {
            return "RDD" + this.model.getValue("clientReference") + " - Edit RDD";
        }
        return "";
    }

    @action
    public setHasLoaded = (val: boolean) => {
        this.hasLoaded = val;
    };

    @action
    public setSnackMessage = (val: string) => {
        this.snackMessage = val;
    };

    @action
    public setSnackType = (val: string) => {
        this.snackType = val;
    };

    @action
    public setSnackbarState = (val: boolean) => {
        this.snackbarState = val;
    };

    @action
    public handleSupplierId = (item: { id: string; displayName: string }) => {
        this.supplier = item;
        this.setValue("supplierId", item.id);
    };

    @action
    public handleCategoryId = (item: { id: string; displayName: string }) => {
        this.category = item;
        this.setValue("rddV2CategoryId", item.id);
    };

    @action
    public handleStatusId = (item: { id: string; displayName: string }) => {
        this.status = item;
        this.setValue("rddV2StatusId", item.id);
    };

    @action
    public handleRequestedById = (item: { id: string; displayName: string }) => {
        this.requestedBy = item;
        this.setValue("requestedByUserId", item.id);
    };

    @observable
    public hasRequestedDateChanged: boolean = false;

    @observable
    public hasResponseRequiredByDateChanged: boolean = false;

    @action
    public setRequestedDate = (value: string | null): void => {
        this.setValue("requestedDate", value);
        this.hasRequestedDateChanged = true;
    };

    @action
    public setResponseRequiredByDate = (value: string | null): void => {
        this.setValue("responseRequiredByDate", value);
        this.hasResponseRequiredByDateChanged = true;
    };

    @action
    public reset = () => {
        this.model.reset();
        this.server.reset();
    };

    @action
    public handleCancel(projectId: string | null): void {
        this.reset();
        this.history.push(AppUrls.Client.Project.ReviewableDesignDataV2.replace(":projectid", projectId ? projectId : this.model.projectId));
    }

    @action
    public fileChangeForCategory = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
        if (event.target.files !== null && event.target.value !== null && event.target.files.length > 0) {
            let data: any = {
                fileName: event.target.files[0].name,
                formFile: event.target.files[0],
            };
            event.target.value = "";
            const apiResult = await this.fileUpload(data);
            if (apiResult && apiResult.wasSuccessful) {
                runInAction(() => {
                    let model = new RDDV2FormDocumentModel();
                    model.rddV2Id = this.model.id ? this.model.id : "";
                    model.rddV2CategoryId = this.model.rddV2CategoryId ? this.model.rddV2CategoryId : "";
                    model.fileName = data.fileName;
                    model.fileURL = apiResult.payload;
                    model.isDeleted = false;
                    model.createdByUserId = null;
                    this.model.rddV2Documents.push(model);
                    let viewModel = new RDDV2FormDocumentViewModel(model);
                    this.rddV2DocumentViewModels.push(viewModel);
                });
            }
        }
    };

    @action
    public fileChange = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
        if (event.target.files !== null && event.target.value !== null && event.target.files.length > 0) {
            let data: any = {
                fileName: event.target.files[0].name,
                formFile: event.target.files[0],
            };
            event.target.value = "";
            const apiResult = await this.fileUpload(data);
            if (apiResult && apiResult.wasSuccessful) {
                runInAction(() => {
                    let model = new RDDV2FormFileModel();
                    model.rddV2Id = "";
                    model.fileName = data.fileName;
                    model.fileURL = apiResult.payload;
                    model.isDeleted = false;
                    model.createdByUserId = null;
                    this.model.rddV2Attachments.push(model);
                    let viewModel = new RDDV2FormFileViewModel(model);
                    this.rddV2FileViewModels.push(viewModel);
                });
            }
        }
    };
    /**
     * Upload a file to azure.
     * @param data The data of the file to be uploaded.
     * @returns apiResult.
     */
    public fileUpload = async (data: any): Promise<ApiResult<any>> => {
        const formData = new FormData();
        formData.append("formFile", data.formFile);
        formData.append("fileName", data.fileName);
        const apiResult = await this.Post<any>(AppUrls.Server.File.UploadFile, formData);
        if (apiResult) {
            if (!apiResult.wasSuccessful) {
                console.log(apiResult.errors);
                runInAction(() => {
                    this.setSnackMessage("Error uploading file please try again.");
                    this.setSnackType(this.SNACKERROR);
                    this.setSnackbarState(true);
                });
            }
        }
        return apiResult;
    };
    /**
     * Download a file that exists in azure.
     * @param fileUrl The URL of the file to be downloaded.
     * @param fileName The name of the file to be downloaded.
     */
    public DownloadFile = async (fileUrl: string, fileName: string): Promise<void> => {
        try {
            const apiResult = await this.Post<Blob>(AppUrls.Server.File.DownloadFile, fileUrl, undefined, { responseType: "blob" });
            const response = apiResult as any;
            const url = window.URL.createObjectURL(new Blob([response]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
        } catch (exception) {
            console.error(exception);
            this.setIsErrored(true);
        }
    };

    @action
    public deleteRDDV2Attachment = async (index: number): Promise<void> => {
        this.rddV2FileViewModels[index].model.isDeleted = true;
    };

    @action
    public deleteRDDV2Document = async (index: number): Promise<void> => {
        this.rddV2DocumentViewModels[index].model.isDeleted = true;
    };

    @computed
    public get isFormDisabled(): boolean {
        return this.model.id !== null && this.model.id !== undefined && this.model.id !== "";
    }

    @computed
    public get hasId(): boolean {
        return this.model.id !== null && this.model.id !== undefined && this.model.id !== "";
    }

    @computed
    public get getTodayDateFormatted(): string {
        return this.model.createdDate ? moment(this.model.createdDate).format("DD/MM/YYYY @ HH:mm").toString() : moment().format("DD/MM/YYYY @ HH:mm").toString();
    }

    @action
    private createViewModels() {
        for (const fileItem of this.model.rddV2Attachments) {
            this.rddV2FileViewModels.push(new RDDV2FormFileViewModel(fileItem));
        }
        for (const rddV2Document of this.model.rddV2Documents) {
            this.rddV2DocumentViewModels.push(new RDDV2FormDocumentViewModel(rddV2Document));
        }
    }

    @computed
    public get getProjectReferenceFormatted(): string {
        if (!this.rddV2ProjectDetails) {
            return "";
        }
        return this.rddV2ProjectDetails.projectReference;
    }

    @computed
    public get getProjectName(): string {
        if (!this.rddV2ProjectDetails) {
            return "";
        }
        return this.rddV2ProjectDetails.projectName;
    }

    @computed
    public get getSupplier(): string | undefined {
        return this.suppliers.find((s) => s.id === this.model.supplierId)?.displayName;
    }

    @observable
    public isFromDarwinOptions = observable<{ id: boolean; displayName: string }>([
        { id: true, displayName: "From Darwin" },
        { id: false, displayName: "From Supplier" },
    ]);

    @computed
    public get isFromDarwinOption() {
        return this.isFromDarwinOptions.find((o) => o.id === this.model.isFromDarwin);
    }

    @action
    public setIsFromDarwin = (isFromDarwin: boolean) => {
        this.model.isFromDarwin = isFromDarwin;

        if (isFromDarwin) {
            this.supplier = null;
            this.setValue("supplierId", RDDV2FormModel.DEFAULT_SUPPLIER_ID);
            this.setValue("supplierReference", RDDV2FormModel.DEFAULT_SUPPLIER_REFERENCE);
        }
    };

    public loadWithRelated = async (): Promise<void> => {
        this.setIsLoading(true);
        return await this.server
            .query<RDDV2AndRelatedResponseDTO>(
                () => this.Get(`${AppUrls.Server.Projects.ProjectTrackers.RDDV2.GetRDDV2AndRelated}\\${this.model.id}`),
                (result) => {
                    runInAction(() => {
                        this.model.fromDto(result);
                        this.categories.replace(result.categories);
                        this.suppliers.replace(result.suppliers);
                        this.statuses.replace(result.statuses);
                        this.rddV2ProjectDetails = result.rddV2ProjectDetails;
                        this.createViewModels();
                        const selectedSupplier: RDDV2SupplierDTO | undefined = this.suppliers.find((t) => t.id === this.model.supplierId);
                        const selectedStatus: RDDV2StatusDTO | undefined = this.statuses.find((t) => t.id === this.model.rddV2StatusId);
                        const selectedCategory: RDDV2CategoryDTO | undefined = this.categories.find((t) => t.id === this.model.rddV2CategoryId);
                        const selectedRequestedByUser: RDDV2RequestedByDTO | undefined = this.requestedByUsers.find((t) => t.id === this.model.requestedByUserId);

                        if (selectedSupplier) {
                            this.handleSupplierId(selectedSupplier);
                        }
                        if (selectedStatus) {
                            this.handleStatusId(selectedStatus);
                        }
                        if (selectedCategory) {
                            this.handleCategoryId(selectedCategory);
                        }
                        if (selectedRequestedByUser) {
                            this.handleRequestedById(selectedRequestedByUser);
                        }
                    });
                },
            )
            .finally(() => {
                this.setIsLoading(false);
                this.setHasLoaded(true);
            });
    };

    public upsert = async (isrevision: any): Promise<void> => {
        this.setIsLoading(true);
        const model: RDDV2DTO = this.model.toDto();
        console.log("model: " + model);
        model.supplierReference = this.model.supplierReference;

        let attachmentsToAdd: RDDV2FileDTO[] = [];
        let documentsToAdd: RDDV2DocumentDTO[] = [];

        this.rddV2FileViewModels.forEach((item) => {
            const attachmentToAdd: RDDV2FileDTO = item.model.toDto();
            attachmentToAdd.rddV2Id = model.id;
            attachmentsToAdd.push(attachmentToAdd);
        });

        this.rddV2DocumentViewModels.forEach((item) => {
            const documentToAdd: RDDV2DocumentDTO = item.model.toDto();
            documentToAdd.rddV2Id = model.id;
            documentToAdd.rddV2CategoryId = model.rddV2CategoryId;
            documentsToAdd.push(documentToAdd);
        });

        const request: UpsertRDDV2AndRelatedRequestDTO = {
            rddV2: model,
            rddV2Attachments: attachmentsToAdd,
            rddV2Documents: documentsToAdd,
        };
        return await this.server
            .command<RDDV2AndRelatedResponseDTO>(
                () => this.Post(AppUrls.Server.Projects.ProjectTrackers.RDDV2.Upsert, request),
                (result: RDDV2AndRelatedResponseDTO) => {
                    runInAction(() => {
                        if (result) {
                            this.handleCancel(result.rddV2.projectId);
                        }
                    });
                },
                this.isMyModelValid,
                "There was an error trying to send the Reviewable Design Data",
            )
            .finally(() => this.setIsLoading(false));
    };

    private isMyModelValid = async (): Promise<boolean> => {
        let isValid = true;

        if ((await this.isModelValid()) === false) {
            isValid = false;
        }

        return isValid;
    };

    public async isFieldValid(fieldName: keyof FieldType<RDDV2FormModel>): Promise<boolean> {
        let { isValid, errorMessage } = await this.validateDecorators(fieldName);

        this.setError(fieldName, errorMessage);
        this.setValid(fieldName, isValid);

        return isValid;
    }

    public afterUpdate: undefined;
    public beforeUpdate: undefined;
}
